import React, { useImperativeHandle, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Pageable } from '@api/ringil-api.v2';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { isNullOrUndefined } from '@utils/utils';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import useMassAction from '@hooks/useMassAction/useMassAction.tsx';
import { FilterPage, FilterSort, useFilter } from '@hooks/useFilter/Filter.tsx';
import { ActionPanel, hasInspectableMassAction, MassActionConfig } from './RingilMassActions';
import { FilterConfig, FilterUiVersionSwitch, getArrayFilterFields, getFilterFields, RingilFilterBar, useFilterUiVersion } from '@components/RingilFilters';
import { useMediaQuery } from 'react-responsive';
import { useSecurityMode } from '@components/security/SecurityModeContext';
import RingilPaginator from './RingilPaginator';
import { withoutEmptyValues } from './withoutEmptyValues';
import { TableExportConfig, useTableExport } from './useTableExport';

// The filtering UI lives in @components/RingilFilters (it is not table-specific); these types are
// re-exported for the ~28 existing consumers that import them from this module.
export { FilterType, DateTimeFormatType } from '@components/RingilFilters';
export type { OptionType, FilterConfig, SavedFilter } from '@components/RingilFilters';

export {
  MobileCard,
  MobileCardHeader,
  MobileCardContent,
  MobileSection,
  MobileSectionTitle,
  MobileInfoTag,
  MobileFileTag,
  MobileLabelDiv
} from './MobileComponents';

interface SavedFiltersResponse<TFilter> {
  filters: Array<{
    id: string;
    name: string;
    data: TFilter;
    sort?: {
      sortField: string;
      sortDirection: 'ASC' | 'DESC'
    };
  }>;
}

export interface PagedData<TPagedData> {
  data: TPagedData[];
  total: number;
  page: number;
  size: number;
}

export interface ColumnConfig<TPagedData> {
  id: string;
  field?: string;
  header?: string;
  headerDefault?: string;
  /** When set, rendered instead of tr(header) — for non-string headers (e.g. a timezone hint under the label). */
  headerRender?: () => React.ReactNode;
  width?: string;
  sortable?: boolean;
  isDefaultSort?: boolean;
  /** Direction used when this column is the default sort (isDefaultSort). Defaults to DESC. */
  defaultSortDirection?: 'ASC' | 'DESC';
  feId?: string;
  order?: number;
  hidden?: boolean;
  /**
   * Vertical alignment of the cell body. Defaults to `top`, which suits multi-line cells; `middle`
   * is for rows whose cells are single-line text sitting next to controls, where top-aligned text
   * reads as misaligned against the buttons.
   */
  verticalAlign?: 'top' | 'middle';
  /** `expanded` = the row's expansion is open (for a chevron next to the toggle). */
  render: (row: TPagedData, toggleExpandedRow?: (row: TPagedData) => void, expanded?: boolean) => React.ReactNode;
}

interface ComponentProps<TFilter, TSavedFilter, TPagedData> {
  columnConfig: ColumnConfig<TPagedData>[];
  renderMobileCard?: ((row: TPagedData, checkbox?: JSX.Element | null) => JSX.Element);
  mobileFilter?: Element;
  filtersConf?: FilterConfig[];
  loadDataFromApi: (filter: TFilter, pageable: Pageable, onSuccess: ((pagedData: PagedData<TPagedData>) => void)) => void;
  filterName: string;
  getSavedFilters?: (onSuccess: ((data: SavedFiltersResponse<TFilter>) => void), hashAuthentication?: string, callbacks?: any) => void;
  saveFilters?: (body: {
    name: string,
    data: TFilter
  }, onSuccess: (() => void), hashAuthentication?: string, callbacks?: any) => void;
  deleteSavedFilter?: (filterId: string, onSuccess: (() => void), hashAuthentication?: string, callbacks?: any) => void;
  massActionConfig?: MassActionConfig<TPagedData>[];
  reloadData?: () => void;
  floatingMassActions?: boolean;
  rowExpansionTemplate?: (row: TPagedData, toggleExpandedRow?: (row: TPagedData) => void) => React.ReactNode;
  rowsPerPageOptions?: number[];
  /** Hide the bottom paginator chrome — for single-row / non-paged tables. Default false (paginator shown). */
  hidePaginator?: boolean;
  /** Per-row predicate: rows returning false get no selection checkbox and are excluded from select-all. Default: every row selectable. */
  isRowSelectable?: (row: TPagedData) => boolean;
  /**
   * Message for an empty result, given the filter that produced it — so a page can explain an empty
   * *filtered* result ("nothing matches this filter, here is how to clear it") differently from a
   * genuinely empty table. Returning nothing falls back to the generic "no records found".
   */
  emptyMessage?: (filter: TFilter) => React.ReactNode;
  normalizeFilter?: (filter: TFilter) => TFilter;
  /**
   * Opt this table into the new (v2) filtering UI. When true, a version switch is shown and the
   * globally-stored {@link useFilterUiVersion} flag decides whether the legacy grid or the new
   * chip bar renders. Defaults to true; pass false to keep a table on the legacy filters only.
   */
  enableFilterV2?: boolean;
  /** When provided, a compact export control is shown on the right of the filter panel (generic export API only). */
  exportConfig?: TableExportConfig<TFilter>;

  /** Row count at which the API stops counting; the paginator then shows e.g. "10 000+" rather than an exact total. */
  totalCapAt?: number;
  /** Show a manual "reload" control that re-fetches the current page with the active filters. Default false. */
  enableReload?: boolean;
  /** Custom label for the reload control. Defaults to the translated "Reload". */
  reloadLabel?: string;
}

export type { MassActionConfig };

const RingilDataTable = React.forwardRef(<TFilter, TSavedFilter, TPagedData extends { id?: string }>({
                                                                                                       columnConfig,
                                                                                                       renderMobileCard,
                                                                                                       mobileFilter,
                                                                                                       filtersConf,
                                                                                                       loadDataFromApi,
                                                                                                       filterName,
                                                                                                       getSavedFilters,
                                                                                                       saveFilters,
                                                                                                       deleteSavedFilter,
                                                                                                       massActionConfig,
                                                                                                       floatingMassActions = false,
                                                                                                       rowExpansionTemplate,
                                                                                                       rowsPerPageOptions = [10, 20, 50, 100],
                                                                                                       hidePaginator = false,
                                                                                                       totalCapAt,
                                                                                                       isRowSelectable,
                                                                                                       emptyMessage,
                                                                                                       normalizeFilter,
                                                                                                       exportConfig,
                                                                                                       enableReload = false,
                                                                                                       reloadLabel,
                                                                                                       enableFilterV2 = true,
}: ComponentProps<TFilter, TSavedFilter, TPagedData>,
ref?: React.Ref<{
  reloadData: () => void;
  selectedObjects: TPagedData[];
  clear: () => void;
  applyFilterValue: (property: string, value: any) => void;
}>) => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();

  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<PagedData<TPagedData>>({
    data: [],
    total: 0,
    page: 1,
    size: 10,
  });
  const [expandedRows, setExpandedRows] = useState<TPagedData[]>([]);

  const loadData =(pageSize: number, pageNumber: number, filter: TFilter, sort: FilterSort) => {
    setLoading(true);
    const normalizedFilter = withoutEmptyValues(normalizeFilter ? normalizeFilter(filter) : filter);
    loadDataFromApi(
      normalizedFilter,
      {
        pageSize: pageSize < rowsPerPageOptions[0] ? rowsPerPageOptions[0] : pageSize,
        pageNumber: pageNumber,
        sortField: sort.sortField,
        sortDirection: sort.sortDirection,
      },
      data => {
        // When a filter shrinks the result set below the current page, the requested page no longer
        // exists and the API returns an empty page. Snap back to the first page (which re-fetches via
        // applyPaging) instead of leaving the user stuck on an empty out-of-range page.
        const effectivePageSize = data.size || pageSize;
        const totalPages = Math.max(1, Math.ceil((data.total || 0) / effectivePageSize));
        if (data.total > 0 && pageNumber > totalPages) {
          // Keep the loading state until the reset page re-fetches, so the stale page doesn't flash.
          applyPaging({ pageNumber: 1, pageSize: effectivePageSize });
          return;
        }
        setLoading(false);
        setPageData(data);
        // keep open only the rows still listed, as their fresh objects, so the expansion and its toggle never disagree
        setExpandedRows(prev => (prev.length ? (data.data ?? []).filter(row => prev.some(r => r.id === row.id)) : prev));
      },
    );
  };

  const {
    applyFilterValue,
    filter,
    applyPaging,
    page,
    onPage,
    applySorting,
    sort,
    applyFilter,
  } = useFilter<TFilter>({
      onLoad: (page: FilterPage, filter: TFilter) => {
        reloadData();
      },
      filterName: filterName,
      defaultSort: (() => {
        const defaultSortColumn = columnConfig.find(col => col.isDefaultSort);
        return { sortField: defaultSortColumn?.field ?? columnConfig[0].field, sortDirection: defaultSortColumn?.defaultSortDirection ?? 'DESC' };
      })(),
      // Multi-select / tree filters are array-valued; coerce single URL values back to arrays.
      arrayFields: getArrayFilterFields(filtersConf),
      // The bar's own key list, so a repeated value on a single-value filter collapses before it renders.
      filterKeys: getFilterFields(filtersConf),
    },
  );

  const reloadData = () => {
    loadData(page.pageSize, page.pageNumber, filter, sort);
  };

  useImperativeHandle(ref, () => ({
    reloadData,
    selectedObjects,
    clear,
    // Exposed so a row action ("filter by this value") can drive the filter bar from outside the
    // table; the filter state lives in useFilter here, so the parent has no other way to set a key.
    applyFilterValue,
  }));

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  /** Centred across the full width, so an empty table reads as a statement rather than a stray first cell. */
  const resolvedEmptyMessage = <div className={'w-full py-6 text-center text-content-secondary'}>{emptyMessage?.(filter) ?? t('global.noRecordFound')}</div>;

  // Generic export (GENERIC_EXPORT_API) — rendered as a compact menu in the mass-action bar
  const { exportControl, exportShown } = useTableExport({ exportConfig, filterName, filter, normalizeFilter });

  const reloadControl = enableReload ? (
    <RingilButton
      mode="link"
      label={reloadLabel ?? tr(`RingilDataTable.reload`, `Reload`)}
      icon={<i className="pi pi-refresh" />}
      disabled={loading}
      onClick={() => reloadData()}
    />
  ) : null;

  const rightControls = reloadControl || exportControl ? (
    <>
      {reloadControl}
      {exportControl}
    </>
  ) : null;

  const {
    MassActionCheckboxAndState,
    toggleCheckbox,
    isSelected,
    setSelectedObjects,
    selectedObjects,
    clear,
  } = useMassAction<TPagedData>(
    (a, b) => {
      return getIdTPagedData(a) === getIdTPagedData(b);
    },
    () => null // no-op for renderButtons
  );

  // Owned here (not inside RingilFilterBar) because the version switch renders *below* the table.
  const { version: filterUiVersion, toggle: toggleFilterUiVersion, switchVisible: filterSwitchVisible } = useFilterUiVersion();

  // Read above the early return below — it is a hook.
  const { enabled: securityMode } = useSecurityMode();

  if (isNullOrUndefined(filter)) return <>Loading....</>;

  const handleSelectAllChange = (checked: boolean) => {
    if (checked && pageData?.data) {
      setSelectedObjects(isRowSelectable ? pageData.data.filter(isRowSelectable) : pageData.data);
    } else {
      setSelectedObjects([]);
    }
  };

  const hasVisibleMassAction = hasInspectableMassAction(massActionConfig ?? [], selectedObjects, securityMode);

  const toggleExpandedRow = (rowData: TPagedData) => {
    setExpandedRows(prev => {
      const exists = prev?.some(r => r.id === rowData.id);
      if (exists) {
        return prev?.filter(r => r.id !== rowData.id);
      } else {
        return [...prev, rowData];
      }
    });
  };

  const renderColumns = () =>
    [...columnConfig]
      .filter(column => !column.hidden) // Filter out hidden columns
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(column => (
        <Column
          key={column.id}
          //field={column.field}
          body={data => column.render(data, toggleExpandedRow, expandedRows.some(r => r.id === data.id))}
          header={column.headerRender ? column.headerRender() : tr(column.header, column.headerDefault)}
          style={{ width: column.width }}
          bodyClassName={column.verticalAlign === 'middle' ? 'align-middle' : 'align-top'}
          sortable={column.sortable}
          sortField={column.field}
        />
      ));

  const getIdTPagedData = (row: TPagedData): string | number | undefined => {
    if (row && typeof row === 'object' && 'id' in row) {
      return (row as any).id;
    }
    return undefined;
  };

  return (
    // Scope class opts this table into the legacy PrimeReact overrides in styles/overrides/legacy-fields.scss.
    <div className={'r-legacy-table-fields contents'}>
      <RingilFilterBar
        filtersConf={filtersConf}
        filterName={filterName}
        filter={filter}
        applyFilterValue={applyFilterValue}
        applyFilter={applyFilter}
        loading={loading}
        sort={sort}
        applySorting={applySorting}
        sortableColumns={columnConfig.filter(col => col.sortable && col.field).map(col => ({ field: col.field, label: tr(col.header, col.headerDefault) }))}
        getSavedFilters={getSavedFilters}
        saveFilters={saveFilters}
        deleteSavedFilter={deleteSavedFilter}
        normalizeFilter={normalizeFilter}
        enableFilterV2={enableFilterV2}
        filterUiVersion={filterUiVersion}
      />
      {pageData && (
        <div className="datatable-filter p-datatable-customers">
          {(hasVisibleMassAction || exportShown || enableReload) && (
            <div className="bg-content-quaternary">
              <ActionPanel
                selectedCount={selectedObjects.length}
                totalCount={pageData?.data?.length ?? 0}
                onSelectAllChange={handleSelectAllChange}
                isMobile={isMobile}
                massActionConfig={massActionConfig ?? []}
                selectedObjects={selectedObjects}
                floatingMassActions={floatingMassActions}
                hasMassActions={hasVisibleMassAction}
                rightSlot={rightControls}
                idPrefix={filterName}
              />
              {/* Separator between the mass-action bar and the table header — same as saved filters / filters. */}
              <hr className="mx-4 m-0 border-t border-solid border-content-negativePrimary" />
            </div>
          )}

          {/* Render mobile cards when on mobile and renderMobileCard is provided */}
          {isMobile && renderMobileCard ? (
            <div className="mobile-cards-container">
              {/* Mobile used to render an empty container for an empty page — no message at all. */}
              {pageData.data.length === 0 && resolvedEmptyMessage}
              {pageData.data.map((row, index) => (
                <div key={index} className="mobile-card-wrapper">
                  <div className="mobile-card-content-with-checkbox">
                    <div className="mobile-card-content">
                      {renderMobileCard(row, hasVisibleMassAction && (!isRowSelectable || isRowSelectable(row)) ? (
                        <Checkbox
                          checked={isSelected(row)}
                          onChange={() => toggleCheckbox(row)}
                          className="mobile-card-injected-checkbox"
                        />
                      ) : null)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Render regular DataTable for desktop
            <DataTable
              emptyMessage={resolvedEmptyMessage}
              value={pageData.data}
              rows={pageData.size}
              className="ringil-table"
              paginator={false}
              lazy={true}
              sortMode="single"
              removableSort={false}
              onSort={e => {
                applySorting(e.sortField, e.sortOrder === 1 ? 'ASC' : 'DESC');
              }}
              sortField={sort.sortField}
              sortOrder={sort.sortDirection === 'ASC' ? 1 : -1}
              loading={loading}
              expandedRows={expandedRows}
              rowExpansionTemplate={(row) => rowExpansionTemplate(row, toggleExpandedRow)}
            >
              {hasVisibleMassAction && (
              <Column
                header={''}
                body={(row: TPagedData) =>
                  isRowSelectable && !isRowSelectable(row) ? null : (
                    <Checkbox
                      onChange={e => {
                        toggleCheckbox(row);
                      }}
                      checked={isSelected(row)}
                    />
                  )
                }
                style={{ width: 30 }}
                bodyClassName="align-top"
              />
              )}
              {renderColumns()}
            </DataTable>
          )}
          {!hidePaginator && (
            <RingilPaginator
              totalRecords={pageData.total}
              page={pageData.page}
              pageSize={pageData.size}
              rowsPerPageOptions={rowsPerPageOptions}
              totalCapAt={totalCapAt}
              onPage={onPage}
              isMobile={isMobile}
            />
          )}
        </div>
      )}
      {enableFilterV2 && filtersConf?.length > 0 && filterSwitchVisible && <FilterUiVersionSwitch version={filterUiVersion} onToggle={toggleFilterUiVersion} />}

      <Toast ref={toast} />
    </div>
  );


});


RingilDataTable.displayName = 'RingilDataTable';

export default RingilDataTable as <TFilter, TSavedFilter, TPagedData>(
  props: ComponentProps<TFilter, TSavedFilter, TPagedData> & {
    ref?: React.Ref<{
      reloadData: () => void;
      selectedObjects: TPagedData[];
      clear: () => void;
      /** Always provided by the table; optional here so the ~36 consumers declaring the older three-member ref shape still type-check. */
      applyFilterValue?: (property: string, value: any) => void;
    }>;
  }
) => JSX.Element;


export const BaseTag = styled.div<{
  isExpanded?: boolean;
  hasContent?: boolean;
  clickable?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 8px;
  background-color: transparent;
  border: 1px solid #ffeee5;
  border-radius: 4px;
  color: #616061;
  font-size: 0.875rem;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  transition: all 0.2s ease-in-out;
  box-sizing: border-box;
  line-height: 1.2;
  min-height: 32px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    fill: #616061;
  }

  .tag-label,
  .tag-text {
    display: inline;
    font-family: "DM Sans", serif;
    font-size: 12px;
    font-style: normal;
    letter-spacing: -0.12px;
  }

  .tag-text {
    color: var(--Content-Primary, #343333);
    font-weight: 500;
  }

  .tag-label {
    color: var(--Content-Secondary, #616061);
    font-weight: 400;
  }
`;
